/**
 * Envío libre — correo puntual a un cliente (pruebas SMTP / mensaje ad hoc).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSectionCard } from "@/components/despacho/DespachoUi";
import SpellcheckInput from "@/components/SpellcheckInput";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  emailTransportStatusLabel,
  isGmailEmail,
  smtpPendingHint,
  usesPlatformEmail,
} from "@/lib/smtpPresets";
import {
  despachoMensajesGeneralesApi,
  parseMensajesGeneralesError,
} from "@/modules/despacho/despachoMensajesGeneralesApi";

function clientLabel(row) {
  const name = row?.client_name || "Sin nombre";
  const rfc = row?.client_rfc ? ` · ${row.client_rfc}` : "";
  return `${name}${rfc}`;
}

export default function CalendarioEnvioLibrePanel({ readOnly = false, onOpenParams }) {
  const [loadState, setLoadState] = useState("loading");
  const [clients, setClients] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [clientId, setClientId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [appendFooter, setAppendFooter] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [sending, setSending] = useState(false);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.fiscal_client_id) === String(clientId)) || null,
    [clients, clientId],
  );

  const emailOptions = useMemo(() => {
    const fromClient = (selectedClient?.emails || []).filter(Boolean);
    if (toEmail && !fromClient.includes(toEmail)) {
      return [toEmail, ...fromClient];
    }
    return fromClient;
  }, [selectedClient, toEmail]);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await despachoMensajesGeneralesApi.listFreeSendClients();
      setClients(data.items || []);
      setEmailConfig(data.email_config || null);
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      toast.error(parseMensajesGeneralesError(err).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedClient) {
      setToEmail("");
      return;
    }
    const first = (selectedClient.emails || [])[0] || "";
    setToEmail(first);
  }, [selectedClient?.fiscal_client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClientChange = (value) => {
    setClientId(value);
    const row = clients.find((c) => String(c.fiscal_client_id) === String(value));
    setToEmail((row?.emails || [])[0] || "");
  };

  const handleSend = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente.");
      return;
    }
    if (!toEmail.trim()) {
      toast.error("Indique el correo destino.");
      return;
    }
    if (!subject.trim()) {
      toast.error("Indique el asunto.");
      return;
    }
    if (!body.trim()) {
      toast.error("Escriba el mensaje.");
      return;
    }
    setSending(true);
    try {
      const data = await despachoMensajesGeneralesApi.sendFreeEmail({
        fiscal_client_id: clientId,
        to_email: toEmail.trim(),
        subject: subject.trim(),
        body: body.trim(),
        append_footer: appendFooter,
        dry_run: dryRun,
      });
      const result = data.result || {};
      if (data.ok) {
        toast.success(`Correo enviado a ${data.to_email}`);
      } else if (result.status === "dry_run") {
        if (!dryRun && data.transport_ready === false) {
          const hint = smtpPendingHint(emailConfig) || emailTransportStatusLabel(emailConfig);
          toast.error("El correo aún no puede enviarse.", {
            description: hint,
            action: onOpenParams
              ? { label: "Ir a Parámetros", onClick: onOpenParams }
              : undefined,
          });
        } else {
          toast.message("Simulación: no se envió correo.", {
            description: `Destino: ${data.to_email}`,
          });
        }
      } else if (result.status === "not_configured") {
        toast.error(result.detail || "SMTP no configurado. Complete Parámetros de correo.");
      } else {
        const detail = result.detail || "No se pudo enviar el correo.";
        toast.error(detail);
        if (
          !emailConfig?.use_platform_smtp
          && isGmailEmail(emailConfig?.from_email)
          && /535|contraseña de aplicación|gmail rechazó/i.test(detail)
          && onOpenParams
        ) {
          toast.message("Solución rápida", {
            description: "En Parámetros de correo pulse «Activar servidor de la plataforma y guardar».",
            action: { label: "Ir a Parámetros", onClick: onOpenParams },
          });
        }
      }
    } catch (err) {
      const parsed = parseMensajesGeneralesError(err);
      if (parsed.code === "SMTP_NOT_READY") {
        toast.error(parsed.message || "Complete Parámetros de correo antes de enviar.", {
          action: onOpenParams
            ? { label: "Ir a Parámetros", onClick: onOpenParams }
            : undefined,
        });
      } else {
        toast.error(parsed.message);
      }
    } finally {
      setSending(false);
    }
  };

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6" data-testid="calendario-envio-libre-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando clientes…
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="space-y-3" data-testid="calendario-envio-libre-error">
        <p className="text-sm text-red-700">No se pudo cargar el envío libre.</p>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={load}>
          Reintentar
        </Button>
      </div>
    );
  }

  const pendingTransportHint = !emailConfig?.transport_ready
    ? smtpPendingHint(emailConfig)
    : null;

  return (
    <div className="space-y-4" data-testid="calendario-envio-libre">
      <DespachoInfoCallout>
        Envío puntual como un correo normal: elija cliente, redacte asunto y mensaje.
        Útil para <strong>probar SMTP</strong> o mandar un aviso rápido sin crear campaña en Mensajes generales.
      </DespachoInfoCallout>

      <DespachoSectionCard title="Correo de salida del despacho">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-700">
            Remitente: <strong>{emailConfig?.from_email || "No configurado"}</strong>
            {emailConfig?.from_name ? ` (${emailConfig.from_name})` : ""}
          </span>
          <span className={`text-xs ${emailConfig?.enabled ? "text-emerald-700" : "text-amber-700"}`}>
            {emailConfig?.enabled ? "Envío activo" : "Envío desactivado — active «Activar envíos» en Parámetros"}
          </span>
          <span className={`text-xs ${emailConfig?.transport_ready ? "text-emerald-700" : "text-amber-700"}`}>
            {emailTransportStatusLabel(emailConfig)}
          </span>
          {pendingTransportHint ? (
            <span className="w-full text-xs text-amber-800">{pendingTransportHint}</span>
          ) : null}
          {isGmailEmail(emailConfig?.from_email) && !usesPlatformEmail(emailConfig) ? (
            <span className="text-xs text-amber-800">Gmail: requiere app password o modo plataforma</span>
          ) : null}
          {!readOnly && onOpenParams ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm"
              onClick={onOpenParams}
              data-testid="envio-libre-go-params"
            >
              Ir a Parámetros de correo
            </Button>
          ) : null}
        </div>
      </DespachoSectionCard>

      <DespachoSectionCard title="Mensaje libre">
        <div className="space-y-4 max-w-2xl">
          <div className="space-y-1.5">
            <Label htmlFor="envio-libre-client">Cliente</Label>
            <Select value={clientId || undefined} onValueChange={handleClientChange} disabled={readOnly}>
              <SelectTrigger id="envio-libre-client" className="rounded-sm" data-testid="envio-libre-client-select">
                <SelectValue placeholder="Seleccione empresa…" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((row) => (
                  <SelectItem key={row.fiscal_client_id} value={String(row.fiscal_client_id)}>
                    {clientLabel(row)}
                    {(row.emails || []).length ? ` — ${row.emails[0]}` : " — sin correo en ficha"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClient && !(selectedClient.emails || []).length ? (
              <p className="text-[11px] text-amber-800 m-0">
                Este cliente no tiene correo en ficha; escriba uno manualmente abajo.
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="envio-libre-email">Correo destino</Label>
            {emailOptions.length > 1 ? (
              <Select value={toEmail || undefined} onValueChange={setToEmail} disabled={readOnly}>
                <SelectTrigger id="envio-libre-email" className="rounded-sm" data-testid="envio-libre-email-select">
                  <SelectValue placeholder="Correo…" />
                </SelectTrigger>
                <SelectContent>
                  {emailOptions.map((em) => (
                    <SelectItem key={em} value={em}>
                      {em}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="envio-libre-email"
                type="email"
                className="rounded-sm"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="cliente@empresa.mx"
                disabled={readOnly}
                data-testid="envio-libre-email-input"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="envio-libre-subject">Asunto</Label>
            <SpellcheckInput
              id="envio-libre-subject"
              className="rounded-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Prueba de envío — Despacho BNT"
              disabled={readOnly}
              testId="envio-libre-subject"
              wrapperClassName="space-y-1"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="envio-libre-body">Mensaje</Label>
            <SpellcheckTextarea
              id="envio-libre-body"
              className="min-h-[140px] rounded-sm text-sm"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Escriba el texto del correo…"
              disabled={readOnly}
              data-testid="envio-libre-body"
            />
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-slate-700">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={appendFooter}
                onCheckedChange={(v) => setAppendFooter(Boolean(v))}
                disabled={readOnly}
                data-testid="envio-libre-append-footer"
              />
              Incluir pie del despacho
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={dryRun}
                onCheckedChange={(v) => setDryRun(Boolean(v))}
                disabled={readOnly}
                data-testid="envio-libre-dry-run"
              />
              Solo simular (no enviar)
            </label>
          </div>

          {!readOnly && emailConfig && !emailConfig.transport_ready ? (
            <p className="m-0 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-950">
              {smtpPendingHint(emailConfig) || emailTransportStatusLabel(emailConfig)}
              {onOpenParams ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={onOpenParams}
                  >
                    Abrir Parámetros de correo
                  </button>
                </>
              ) : null}
            </p>
          ) : null}

          {!readOnly ? (
            <Button
              type="button"
              className="rounded-sm bg-[#0F2D5C]"
              onClick={handleSend}
              disabled={sending}
              data-testid="envio-libre-send"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
              ) : (
                <Send className="h-4 w-4 mr-2" aria-hidden />
              )}
              {dryRun ? "Simular envío" : "Enviar correo"}
            </Button>
          ) : null}
        </div>
      </DespachoSectionCard>
    </div>
  );
}
